/**
 * Cliente de Firebase y callables tipadas con el contrato.
 *
 * Las callables se tipan con los tipos de `@caes-payments/shared`, así que si Rubén cambia la forma de
 * una respuesta el front deja de compilar. Es el punto donde el contrato deja de ser documentación.
 */
import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';

import type {
  CancelarRequest,
  CancelarResponse,
  ConfirmRequest,
  ConfirmResponse,
  Criterios,
  FicherosRequest,
  FicherosResponse,
  PreviewResponse,
} from '@caes-payments/shared';

const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
});

export const auth = getAuth(app);
export const db = getFirestore(app);

// Misma región que el setGlobalOptions de functions/. Si no coinciden, la llamada da 404 y el mensaje
// no dice que el problema es la región.
const functions = getFunctions(app, 'europe-west1');

if (import.meta.env.DEV) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

/**
 * Preview. Es una MUTACION, no una query: nada la debe reejecutar por su cuenta. Un refetch en
 * background acuñaría un previewId nuevo mientras la pantalla sigue mostrando las cifras viejas, y
 * Ops confirmaría un conjunto que no aprobó.
 *
 * El payload son los criterios DIRECTAMENTE, sin envolver: es lo que espera `handlePreview`.
 * Los nombres de las callables son los de los exports de `functions/src/index.ts` — `previewCallable`,
 * no `preview`. Si se renombran allí, aquí da 404.
 */
export const llamarPreview = httpsCallable<Criterios, PreviewResponse>(functions, 'previewCallable');

/**
 * Preview del programa soft killer (BB-2022/BB-2004). Callable SEPARADA en el backend, que fuerza
 * `programa: 'softKiller'` ignorando lo que se mande. Misma forma de respuesta que `/preview`, y
 * sus previewIds se confirman con el mismo `/confirm` (desde el 2026-10-09, BB-2003).
 */
export const llamarPreviewSoftKiller = httpsCallable<Criterios, PreviewResponse>(
  functions,
  'previewSoftKillerCallable',
);

/**
 * Ficheros de certificación MITECO que la consola está viendo, con su fecha de carga desde Drive
 * (BB-2180, pedido por Javi — ver `ficheros.ts`). Solo lectura sobre el universo completo, sin
 * criterios, sin snapshot y sin audit log: no es un preview. El backend calcula `ultimo`. Request
 * `{}` estricto, según el contrato. Mientras la callable no esté desplegada, la llamada da
 * `not-found` y el bloque lo dice en pantalla, sin tumbar nada.
 */
export const llamarFicheros = httpsCallable<FicherosRequest, FicherosResponse>(functions, 'ficherosCallable');

/**
 * Cuánto espera el navegador a `/confirm`: algo más que los 540 s de `CONFIRM_TIMEOUT_SECONDS` del
 * backend (`functions/src/callables/confirm.ts`). El valor por defecto del SDK son 70 s, y una
 * confirmación de 400.000 viajes tarda ~42–52 s en el servidor: con 70 s el navegador podía abandonar y
 * enseñar un error sobre una confirmación que sí se encoló. Se mueven juntos, y un test de functions
 * lo comprueba leyendo este fichero.
 */
export const TIMEOUT_CONFIRM_MS = 550_000;

/** Confirmación. Manda previewId y nada más (invariantes 3 y 7). */
export const llamarConfirm = httpsCallable<ConfirmRequest, ConfirmResponse>(functions, 'confirmCallable', {
  timeout: TIMEOUT_CONFIRM_MS,
});

/**
 * Cancelar lo que queda por pagar de una confirmación (BB-1676). También solo el `previewId`.
 *
 * Quién puede cancelar lo decide el BACKEND contra `confirm_jobs.actor`. El front esconde la X de las
 * filas ajenas, pero eso es UX: cualquiera podría llamar a esto con el previewId de otra persona, que
 * sale en su propia pantalla del registro.
 */
export const llamarCancelar = httpsCallable<CancelarRequest, CancelarResponse>(functions, 'cancelarCallable');
